import { contactsApi, edgeApi } from '@lib/apiClients';
import { microServicesRoutes } from '@constants/microServicesRoutes';
import { handleApiError } from '@lib/handleApiError';
import type { ApiResponse } from '@types';
import { logError as logErr } from '@lib/logger/logError';
import { logWarning as logWarn } from '@lib/logger/logWarning';
import { logInfo as logInf } from '@lib/logger/logInfo';

export type CallSessionStatus = 'started' | 'paused' | 'finished';
export type SessionActionKind = 'transferred' | 'paused' | 'started' | 'finished';
export type SdrStatus = 'free' | 'busy' | 'on_call' | 'unknown';

export interface SessionAction {
  action: SessionActionKind | Uppercase<SessionActionKind>;
  userId?: string | null;
}

export interface SdrData {
  sdrId: string;
  sdrName: string;
  organizationId: string;
  organizationName: string;
}

export interface CallSessionLite {
  id: string;
  status: CallSessionStatus;
  scriptId?: string | null;
  scriptTitle?: string | null;
  voicemailUrl?: string | null;
  sdrId?: string | null;
  sdrName?: string | null;
  organizationId?: string | null;
  organizationName?: string | null;
  sdrStatus?: SdrStatus | null;
  action?: SessionAction | null;
  speech?: string | null;
  data?: { speech?: string | null } | null;
  voiceNoteId?: string | null;
}

export interface AgentValidationsPayload {
  validationsPassed: boolean;
  sdr?: { data?: Partial<SdrData> | null } | null;
  callSession?: {
    isValid: boolean;
    data?: (Partial<CallSessionLite> & { id?: string }) | null;
  } | null;
  voiceNote?: { data?: { url?: string } | null } | null;
}

export interface NextContactBody {
  id: string;
  name: string;
  jobTitle?: string | null;
  email?: string | null;
  phone: string;
  accountName?: string | null;
  fuegoLead?: boolean | null;
  notes?: string | null;
  notesAgent?: string | null;
  notesSDR?: string | null;
  contactListId: string;
}
export interface ConsumeNextContactResponse {
  body: NextContactBody;
  queueLength: number;
}

export const getAgentCallSessionValidations = async (
  agentId: string,
): Promise<ApiResponse<AgentValidationsPayload>> => {
  try {
    const base = microServicesRoutes.edge.get.agent.callSessionValidations;
    const url = `${base}/${agentId}`;
    const res = await edgeApi.get<AgentValidationsPayload>(url);
    await logInf?.('Fetched agent validations', 'Agent/getAgentCallSessionValidations', { agentId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr?.('{Validations} Error validating agent session', 'Edge/Agent/getAgentCallSessionValidations', { agentId }, false);
    return handleApiError('{Validations} Error validating agent session', e, 'Edge/Agent/getAgentCallSessionValidations');
  }
};

export const consumeNextContact = async (
  callSessionId: string,
  agentId: string
): Promise<ApiResponse<ConsumeNextContactResponse>> => {
  try {
    const base = microServicesRoutes.contacts.get.rabbit.consumeNest;
    const url = `${base}/${callSessionId}/${agentId}`;
    const res = await contactsApi.get<ConsumeNextContactResponse>(url);
    await logInf?.('Consumed next contact', 'Contacts/Rabbit/consume:path', { callSessionId, agentId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logWarn?.('{Rabbit} Error consuming next contact', 'Contacts/Rabbit/consume:path', { callSessionId, agentId }, false);
    return handleApiError('Error consuming next contact', e, 'Contacts/Rabbit/consume:path');
  }
};

export const invalidateCurrentContact = async (
  callSessionId: string,
  agentId: string
): Promise<ApiResponse<boolean>> => {
  try {
    const base = microServicesRoutes.contacts.post.rabbit.invalidate;
    const url = `${base}/${callSessionId}/${agentId}`;
    const res = await contactsApi.post<boolean>(url);
    await logInf?.('Invalidated current contact', 'Contacts/Rabbit/invalidate:path', { callSessionId, agentId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr?.('{Rabbit} Error invalidating current contact', 'Contacts/Rabbit/invalidate:path', { callSessionId, agentId }, false);
    return handleApiError('{Rabbit} Error invalidating current contact', e, 'Contacts/Rabbit/invalidate:path');
  }
};

export const invalidateForEndSession = async (
  callSessionId: string,
): Promise<ApiResponse<boolean>> => {
  try {
    const base = microServicesRoutes.contacts.post.rabbit.invalidateSession;
    const url = `${base}/${callSessionId}`;
    const res = await contactsApi.post<boolean>(url);
    await logInf?.('Invalidated current contact', 'Contacts/Rabbit/invalidate:path', { callSessionId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr?.('{Rabbit} Error invalidating current contact', 'Contacts/Rabbit/invalidate:path', { callSessionId }, false);
    return handleApiError('{Rabbit} Error invalidating current contact', e, 'Contacts/Rabbit/invalidate:path');
  }
};
